import styles from './styles.module.scss'
import OrderListItem from './OrderItem/OrderListItem'
import { NoOrderItem } from './NoOrderItem'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import useSWRInfinite from 'swr/infinite'
import { CustomerApiQueryResponse, Order } from '@components/account/types'
import { makeCustomerRequest } from '@api/graphqlShopify/makeCustomerRequest'
import customerAccountOrdersQuery from '@api/graphqlShopify/queries/customerAccountOrdersQuery'
import { CustomOrder, transformOrder } from './helpers/transformOrder'
import trackError from '@helpers/trackError'
import { parse } from '@helpers/shopify'
import OrderListItemSkeleton from './shared/SkeletonUI/OrderListItemSkeleton'
import { getErrorMessage } from '@api/graphqlShopify'
import { SubmitButton } from '@components/ui/Button'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'

async function fetchOrders(
  cursor: string | null,
  token: string
): Promise<{
  orders: CustomOrder[]
  hasNextPage: boolean
  endCursor: string | null
}> {
  const variables = {
    numOrders: 20,
    cursor
  }

  try {
    const { data, errors } = await makeCustomerRequest<{
      customer: CustomerApiQueryResponse
    }>(customerAccountOrdersQuery, variables, token)

    if (errors) {
      const errorMessage = getErrorMessage(errors)
      throw errorMessage
    }

    if (!data?.customer?.orders) {
      return {
        orders: [],
        hasNextPage: false,
        endCursor: null
      }
    }

    const cleanedOrders = parse(data?.customer?.orders?.edges ?? []) as Order[]

    const orders = cleanedOrders?.map(transformOrder)
    const hasNextPage = data?.customer?.orders?.pageInfo?.hasNextPage ?? false
    const endCursor = data?.customer?.orders?.pageInfo?.endCursor ?? null
    return { orders, hasNextPage, endCursor }
  } catch (e) {
    const error = e as Error
    trackError(new Error('Failed to retrieve orders'), {
      token,
      error,
      section: 'Account Page'
    })
    throw e
  }
}

function AccountOrders() {
  const { token, makeRequestWithRefreshCheck } = useAuth()

  const { data, error, size, setSize, isValidating } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (!token) return null // Prevent fetching until token is available

      if (pageIndex === 0) {
        return ['account-orders', null, token] // initial load
      }

      if (!previousPageData?.hasNextPage) return null

      return ['account-orders', previousPageData?.endCursor ?? null, token]
    },
    ([_, cursor]) => makeRequestWithRefreshCheck(fetchOrders, cursor),
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false
    }
  )

  if (!data && isValidating) {
    return (
      <div className={styles.AccountOrders}>
        <OrderListItemSkeleton />
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.AccountOrders}>
        <NoOrderItem variant="error" />
      </div>
    )
  }

  const allOrders = data?.flatMap(page => page.orders) ?? []
  const hasMore = data?.[data.length - 1]?.hasNextPage

  if (allOrders.length === 0) {
    return (
      <div className={styles.AccountOrders}>
        <NoOrderItem />
      </div>
    )
  }

  const handleOnClick = async ({ event }: SubmitButtonHandlers) => {
    event.preventDefault()
    setSize(size + 1)
  }

  return (
    <>
      <div className={styles.AccountOrders}>
        {allOrders.map((order, index) => (
          <OrderListItem order={order} key={`${order.id}-${index}`} />
        ))}
        {hasMore && (
          <div className={styles.AccountOrders__loadMore}>
            <SubmitButton
              variant="secondary"
              onClick={handleOnClick}
              disabled={isValidating || !hasMore}
              state={isValidating ? 'loading' : 'ready'}
            >
              Load more
            </SubmitButton>
          </div>
        )}
      </div>
    </>
  )
}
export default AccountOrders
